import { LightningIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { cn } from 'app/lib/utils'
import { ChoiceSelect, Field } from '../inspectors/fields'

export type LocationChoice = { id: string; name?: string | null }

/** Stands where the trigger card will go on a workflow that has none yet. The same footprint and
 *  register as a real node card, dashed, so the canvas says "your first block goes here" the way
 *  every builder people already know does. Asks for the location first when there is a choice to
 *  make, because nothing can save without one, then offers the trigger as a full-size action. */
export function TriggerPlaceholder({
  locations,
  locationId,
  onLocationChange,
  picking,
  onChooseTrigger,
  headless,
}: {
  locations: LocationChoice[]
  locationId?: string
  onLocationChange: (id: string) => void
  /** The picker is open in the inspector, so the card shows it is the thing being decided. */
  picking: boolean
  onChooseTrigger: () => void
  /** Steps exist but nothing starts them (the trigger was deleted). */
  headless: boolean
}) {
  const needsLocation = locations.length > 1 && !locationId
  return (
    <div
      className={cn(
        `w-72 rounded-xl border border-dashed bg-surface p-3 shadow-sm`,
        picking ? `border-blue-400 ring-2 ring-blue-400 ring-offset-1` : `border-strong`,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-subtle">
          <LightningIcon size={18} weight="bold" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold tracking-wide text-minimal uppercase">Trigger</div>
          <div className="text-sm font-semibold text-default">
            {headless ? `Nothing starts this yet` : `Start here`}
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-3">
        {needsLocation && (
          <Field label="Location">
            <ChoiceSelect
              value={locationId ?? ``}
              placeholder="Choose a location"
              choices={locations.map((location) => ({ value: location.id, label: location.name || location.id }))}
              onChange={onLocationChange}
            />
          </Field>
        )}
        <Button variant="primary" size="md" className="w-full" onClick={onChooseTrigger}>
          Choose a trigger
        </Button>
      </div>
    </div>
  )
}
